<script setup lang="ts">
import type { WikiVehicle } from '~~/types/gamedata';

const props = withDefaults(defineProps<{
  raw: WikiVehicle | null;
  /** Kurzfassung für die Defense-Card-Kurzübersicht — ohne Resistenz-/Boost-Detailpills. */
  compact?: boolean;
}>(), { compact: false });

function formatNumber(value: number, maximumFractionDigits = 2): string {
  return new Intl.NumberFormat('de-DE', { maximumFractionDigits }).format(value);
}

const weaponOverviewRows = computed(() => getWeaponOverviewRows(props.raw?.weaponry));
const missileDamage = computed(() => getMissileDamage(props.raw?.weaponry));

const SHIELD_RESISTANCE_LABELS: Record<string, string> = {
  physical: 'Physical',
  energy: 'Energy',
  distortion: 'Distortion',
};

const shieldResistancePills = computed(() => {
  const resistance = props.raw?.shield?.resistance;
  if (!resistance) return [];
  return (['physical', 'energy', 'distortion'] as const)
    .map((key) => {
      const value = resistance[key]?.maximum;
      return value == null ? null : { label: SHIELD_RESISTANCE_LABELS[key], value: `${formatNumber(value * 100, 0)}%` };
    })
    .filter((pill): pill is { label: string; value: string } => pill !== null);
});

/** Shield-Type-Kürzel (z.B. "Bubble" → "B") für das Badge neben HP/Regen — dieselbe Kennung wie bei FleetYards, voller Name als Tooltip. */
const shieldFaceType = computed(() => props.raw?.shield?.face_type?.trim() ?? null);
const shieldFaceTypeInitial = computed(() => shieldFaceType.value?.charAt(0)?.toUpperCase() ?? null);

function boostPercent(base: number | null | undefined, boosted: number | null | undefined): number | null {
  if (base == null || boosted == null || base === 0) return null;
  return Math.round(((boosted - base) / base) * 100);
}

const boostAngularVelocityPills = computed(() => {
  const agility = props.raw?.agility;
  return [
    { label: 'Pitch', value: boostPercent(agility?.pitch, agility?.pitch_boosted) },
    { label: 'Yaw', value: boostPercent(agility?.yaw, agility?.yaw_boosted) },
    { label: 'Roll', value: boostPercent(agility?.roll, agility?.roll_boosted) },
  ]
    .filter((stat): stat is { label: string; value: number } => stat.value !== null)
    .map((stat) => ({ label: stat.label, value: `+${stat.value}%` }));
});

const hasOverview = computed(() => !!(
  weaponOverviewRows.value.length
  || missileDamage.value
  || props.raw?.shield?.hp != null
  || boostAngularVelocityPills.value.length
));
</script>

<template>
  <div v-if="hasOverview" class="flex flex-wrap gap-x-10 gap-y-5">
    <div :class="compact ? 'w-full space-y-4' : 'w-full @lg:w-[calc(50%-1.25rem)] space-y-4'">
      <div v-if="weaponOverviewRows.length" class="space-y-2">
        <div
          v-for="row in weaponOverviewRows"
          :key="row.key"
          class="flex items-center gap-4"
          :class="row.muted ? 'opacity-35' : ''"
        >
          <ShipexkursSystemIcon :name="row.icon" class="size-5 shrink-0 text-aris-400" />
          <span class="w-12 shrink-0 text-xs text-neutral-500">{{ row.label }}</span>
          <span class="flex items-baseline gap-1">
            <span class="text-xl font-bold text-white">{{ formatNumber(row.dps) }}</span>
            <span class="text-xs text-neutral-500">DPS</span>
          </span>
          <span class="flex items-baseline gap-1">
            <span class="text-xl font-bold text-white">{{ formatNumber(row.alpha) }}</span>
            <span class="text-xs text-neutral-500">Alpha</span>
          </span>
        </div>
      </div>

      <div v-if="missileDamage" class="flex items-center gap-4">
        <ShipexkursSystemIcon name="missile" class="size-5 shrink-0 text-aris-400" />
        <span class="w-12 shrink-0 text-xs text-neutral-500">Raketen</span>
        <span class="flex items-baseline gap-1">
          <span class="text-xl font-bold text-white">{{ formatNumber(missileDamage) }}</span>
          <span class="text-xs text-neutral-500">dmg gesamt</span>
        </span>
      </div>
    </div>

    <div :class="compact ? 'w-full space-y-4' : 'w-full @lg:w-[calc(50%-1.25rem)] space-y-4'">
      <div v-if="raw?.shield?.hp != null" class="space-y-3">
        <div class="flex items-center gap-4">
          <UTooltip :text="shieldFaceType ? `${shieldFaceType}-Schild` : 'Schildtyp unbekannt'">
            <div class="flex size-8 shrink-0 cursor-help items-center justify-center rounded-full border border-aris-400/60 text-xs font-bold text-aris-400">
              {{ shieldFaceTypeInitial ?? 'S' }}
            </div>
          </UTooltip>
          <span class="flex items-baseline gap-1">
            <span class="text-xl font-bold text-white">{{ formatNumber(raw.shield.hp) }}</span>
            <span class="text-xs text-neutral-500">hp</span>
          </span>
          <span v-if="raw.shield.regeneration != null" class="flex items-baseline gap-1">
            <span class="text-xl font-bold text-white">{{ formatNumber(raw.shield.regeneration) }}</span>
            <span class="text-xs text-neutral-500">hp/s</span>
          </span>
        </div>

        <template v-if="!compact">
          <ShipexkursDefensePillGroup label="Shield Resistance" :items="shieldResistancePills" class="pl-12" />

          <p v-if="raw.shield.regeneration_time != null" class="!my-0 flex items-center gap-2 pl-12 text-sm">
            <UIcon name="i-lucide-chevrons-right" class="size-4 shrink-0 text-aris-400" />
            <span class="text-aris-400">Regen</span>
            <span class="font-medium text-white">{{ formatNumber(raw.shield.regeneration_time, 1) }}s</span>
          </p>
        </template>
      </div>

      <ShipexkursDefensePillGroup
        v-if="!compact"
        label="Boost - Angular Velocity"
        :items="boostAngularVelocityPills"
      />
    </div>
  </div>
  <p v-else class="px-4 py-6 text-sm text-neutral-500">Keine Übersichtsdaten verfügbar.</p>
</template>
